import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from 'react-query';
// Removed useDrag, useDrop as they are now in subcomponents
import { planningAPI } from '../../services/api';
import { formatDisplayDate } from '../../utils/timeUtils'; // getDayOfWeekIndex, formatTime, isTimeInRange removed as they seem unused here
import { BOAT_TYPES } from '../../utils/boatTypes';

// Import subcomponents
import CrewRowerSelectionStep from './ScheduleModal/CrewRowerSelectionStep';
import CoachSelectionStep from './ScheduleModal/CoachSelectionStep';
import SummaryValidationStep from './ScheduleModal/SummaryValidationStep';

// Main Multi-Step Schedule Modal Component
const MultiStepScheduleModal = ({ session, timeSlot, onSchedule, onClose }) => {
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Data states
  const [crews, setCrews] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [availableRowers, setAvailableRowers] = useState([]);
  const [showAllRowers, setShowAllRowers] = useState(false);

  // Selection states
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [crewLineups, setCrewLineups] = useState({});

  // Load initial data
  useEffect(() => {
    if (session?.id) {
      loadModalData();
    }
  }, [session?.id]);

  // Update coaches when crew selections change (Step 1 -> Step 2 transition)
  useEffect(() => {
    if (crews.length > 0 && (selectedCrews.length > 0 || Object.keys(crewLineups).length > 0)) {
      updateCoachesBasedOnSelections();
    }
  }, [selectedCrews, crewLineups, crews, timeSlot.date, timeSlot.time]); // Added dependencies for updateCoachesBasedOnSelections

  const loadModalData = async () => {
    try {
      setLoading(true);

      // Get session configuration data first
      const sessionConfig = await planningAPI.getSessionConfiguration(session.id);
      const crewData = sessionConfig.crews || [];
      setCrews(crewData);

      // Set startTime and duration for availability checks
      const sessionStartTime = timeSlot.time;

      // Validate sessionStartTime format
      if (
        !sessionStartTime ||
        (typeof sessionStartTime !== 'string' && typeof sessionStartTime !== 'number')
      ) {
        console.error('Invalid sessionStartTime:', sessionStartTime, 'timeSlot:', timeSlot);
        setErrorMessage('Invalid time slot format. Please try again.');
        return;
      }

      console.log('DEBUG: Loading modal data with parameters:', {
        sessionId: session.id,
        sessionStartTime,
        sessionStartTimeType: typeof sessionStartTime,
        timeSlotDate: timeSlot.date,
        timeSlotTime: timeSlot.time,
      });

      console.log('Loading modal data for session:', session.id, 'at', sessionStartTime);

      // Get rower availability data (always fetch all rowers, filter in frontend)
      const rowerResponse = await planningAPI.getRowerAvailability(
        session.id,
        sessionStartTime,
        null
      );
      setAvailableRowers(rowerResponse.rowers || []);

      // Get coach availability data
      const coachResponse = await planningAPI.getCoachAvailability(
        session.id,
        sessionStartTime,
        null
      );
      setCoaches(coachResponse.coaches || []);

      console.log('Modal data loaded successfully:', {
        crews: crewData.length,
        coaches: coachResponse.coaches?.length || 0,
        rowers: rowerResponse.rowers?.length || 0,
      });
    } catch (error) {
      console.error('Error loading modal data:', error);
      setErrorMessage('Failed to load scheduling data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllRowers = (showAll) => {
    setShowAllRowers(showAll);
    console.log('Show all rowers toggled to:', showAll);
  };

  const updateCoachesBasedOnSelections = async () => {
    try {
      const trainingGroupIds = new Set();
      selectedCrews.forEach((crewId) => {
        const crew = crews.find((c) => c.id === crewId);
        if (crew && crew.training_group_id) {
          trainingGroupIds.add(crew.training_group_id);
        }
      });

      const sessionStartTime = timeSlot.time; // Ensure sessionStartTime is set from timeSlot

      // Validate sessionStartTime format
      if (
        !sessionStartTime ||
        (typeof sessionStartTime !== 'string' && typeof sessionStartTime !== 'number')
      ) {
        console.error(
          'Invalid sessionStartTime in updateCoaches:',
          sessionStartTime,
          'timeSlot:',
          timeSlot
        );
        return;
      }

      // Get coaches based on training groups (if any selected)
      const trainingGroupId = trainingGroupIds.size > 0 ? Array.from(trainingGroupIds)[0] : null;

      console.log('DEBUG: Updating coaches with parameters:', {
        sessionId: session.id,
        sessionStartTime,
        trainingGroupId,
        selectedCrews: selectedCrews.length,
      });

      const response = await planningAPI.getCoachAvailability(
        session.id,
        sessionStartTime,
        trainingGroupId
      );
      setCoaches(response.coaches || []);
      console.log('Coaches updated:', (response.coaches || []).length);
    } catch (error) {
      console.error('Error updating coaches:', error);
    }
  };

  const handleCrewToggle = (crewId) => {
    const isCurrentlySelected = selectedCrews.includes(crewId);

    setSelectedCrews((prev) =>
      isCurrentlySelected ? prev.filter((id) => id !== crewId) : [...prev, crewId]
    );

    const crew = crews.find((c) => c.id === crewId);
    if (crew && crew.is_experimental) {
      if (isCurrentlySelected) {
        // Remove crew lineup data when unselecting
        setCrewLineups((prev) => {
          const newLineups = { ...prev };
          delete newLineups[crewId];
          return newLineups;
        });
      } else {
        // Add crew lineup data when selecting
        const boatType = BOAT_TYPES[crew.boat_type];
        const seatsCount = boatType?.seats || 0;
        const hasCox = boatType?.hasCox || false;
        const totalPositions = seatsCount + (hasCox ? 1 : 0);
        setCrewLineups((prev) => ({
          ...prev,
          [crewId]: new Array(totalPositions).fill(null),
        }));
      }
    }
  };

  const handleCoachToggle = (coachId) => {
    setSelectedCoaches((prev) =>
      prev.includes(coachId) ? prev.filter((id) => id !== coachId) : [...prev, coachId]
    );
  };

  const handleSchedule = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      // Build schedule data for the new session splitting API
      const scheduleData = {
        crew_ids: selectedCrews,
        coaches: selectedCoaches.map((coachId) => {
          const coach = coaches.find((c) => c.id === coachId);
          return {
            user_id: coachId,
            role: 'coach',
          };
        }),
        start_time: timeSlot.time,
        date: timeSlot.date,
        lineups: {},
      };

      // Add lineup data for experimental crews
      selectedCrews.forEach((crewId) => {
        const crew = crews.find((c) => c.id === crewId);
        if (!crew) return;

        if (crew.is_experimental) {
          const lineup = crewLineups[crewId] || [];
          scheduleData.lineups[crewId] = lineup
            .map((rower, index) => {
              if (rower) {
                // Cox position is at index 0 and should have seat_number 0
                // Regular rower positions start at index 1 for boats with cox, or index 0 for boats without cox
                const boatType = BOAT_TYPES[crew.boat_type];
                const seat_number = boatType?.hasCox ? index : index + 1;

                return {
                  rower_id: rower.id,
                  seat_number,
                  side: rower.preferred_side || 'either',
                };
              }
              return null;
            })
            .filter(Boolean);
        } else {
          // For non-experimental crews, use existing lineup
          if (crew.current_lineup) {
            scheduleData.lineups[crewId] = crew.current_lineup.map((assignment) => ({
              rower_id: assignment.user_id,
              seat_number: assignment.seat_number,
              side: assignment.side || 'either',
            }));
          }
        }
      });

      console.log('Scheduling session with data:', scheduleData);

      // Use the new session splitting API
      const saveResult = await planningAPI.scheduleSessionCrews(session.id, scheduleData);

      if (saveResult.success) {
        // Invalidate queries to refresh training sessions data
        queryClient.invalidateQueries('trainingSessions');
        onSchedule(saveResult);
      } else {
        setErrorMessage('Failed to schedule session.');
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      setErrorMessage(
        error.response?.data?.detail || 'Failed to schedule session. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Crew & Rower Selection';
      case 2:
        return 'Coach Selection';
      case 3:
        return 'Review & Schedule';
      default:
        return 'Schedule Session';
    }
  };

  if (loading && crews.length === 0 && coaches.length === 0 && availableRowers.length === 0) {
    // Adjusted loading condition
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="rounded-lg bg-white p-8">
          <div className="flex items-center space-x-3">
            <svg
              className="h-5 w-5 animate-spin text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading schedule data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-lg bg-white">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{getStepTitle()}</h2>
              <div className="mt-1 text-sm text-gray-600">
                <span className="font-medium">{session.title}</span>
                <span className="mx-2">•</span>
                <span>
                  {formatDisplayDate(new Date(timeSlot.date))} at {timeSlot.time}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-4 flex items-center justify-center">
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                      step === currentStep
                        ? 'bg-blue-600 text-white'
                        : step < currentStep
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`h-0.5 w-12 ${
                        step < currentStep ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="max-h-[calc(90vh-200px)] overflow-y-auto p-6">
          {errorMessage && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            </div>
          )}

          {currentStep === 1 && (
            <CrewRowerSelectionStep
              crews={crews}
              availableRowers={availableRowers}
              selectedCrews={selectedCrews}
              crewLineups={crewLineups}
              onCrewToggle={handleCrewToggle}
              onLineupChange={setCrewLineups}
              onShowAllChange={handleShowAllRowers}
              onNext={() => setCurrentStep(2)}
              showAll={showAllRowers}
            />
          )}
          {currentStep === 2 && (
            <CoachSelectionStep
              coaches={coaches}
              selectedCoaches={selectedCoaches}
              onCoachToggle={handleCoachToggle}
              onNext={() => setCurrentStep(3)}
              onBack={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 3 && (
            <SummaryValidationStep
              session={session}
              timeSlot={timeSlot}
              selectedCrews={selectedCrews}
              crewLineups={crewLineups}
              selectedCoaches={selectedCoaches}
              crews={crews}
              coaches={coaches}
              availableRowers={availableRowers} // Prop is passed, though marked as unused in SummaryValidationStep
              onSchedule={handleSchedule}
              onBack={() => setCurrentStep(2)}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MultiStepScheduleModal.propTypes = {
  session: PropTypes.object.isRequired,
  timeSlot: PropTypes.object.isRequired,
  onSchedule: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MultiStepScheduleModal;
